import { useRequest } from 'ahooks'
import { toast } from 'react-toastify'
import { useBtc } from '@/context/BtcContext'

interface TokenBalance {
  token: string
  tokenType: string
  balance: string
  availableBalance: string
  transferBalance: string
}

interface BrcBalanceListResponse {
  code: string
  msg: string
  data: [
    {
      page: string
      limit: string
      totalPage: string
      balanceList: TokenBalance[]
    },
  ]
}

const useBrcTokenBalance = (ticker: string) => {
  const { isConnected, address } = useBtc()

  return useRequest(
    async () => {
      const resp = await fetch(
        `https://api.multibit.exchange/vendor/api/btc/address-balance-list?address=${address}&token=${ticker}`,
      )

      const jsonResp = await resp.json()

      return jsonResp?.data[0]?.balanceList[0] as TokenBalance
    },
    {
      ready: isConnected && !!address && !!ticker,
      refreshDeps: [address, ticker],
    },
  )
}

const fetchBrcBalanceList = async (address: string, page?: number): Promise<BrcBalanceListResponse> => {
  const res = await fetch(
    `https://api.multibit.exchange/vendor/api/btc/address-balance-list?address=${address}&page=${page}&limit=100`,
  )
  return await res.json()
}

// Async function to get data from all pages
async function getAllPageData(address: string): Promise<Record<string, TokenBalance>> {
  // let allData: TokenBalance[] = []
  const allData: Record<string, TokenBalance> = {}
  let currentPage = 1

  // Recursive function to fetch data from all pages
  async function fetchDataRecursive() {
    const response = await fetchBrcBalanceList(address, currentPage)

    // Add the data from the current page to the result object with tokens as keys
    response.data[0].balanceList.forEach((tokenBalance) => {
      allData[tokenBalance.token] = tokenBalance
    })

    // Check if there is a next page
    if (currentPage < parseInt(response.data[0].totalPage)) {
      // Increment the page and continue fetching data for the next page
      currentPage++
      await fetchDataRecursive()
    }
  }

  // Start fetching data
  await fetchDataRecursive()

  return allData
}

const useBrcBalanceList = () => {
  const { isConnected, address } = useBtc()

  return useRequest(
    async () => {
      if (!address) {
        return Promise.reject(new Error('Walleet not connected'))
      }
      return getAllPageData(address)
    },
    {
      ready: isConnected && !!address,
      refreshDeps: [address],
      cacheKey: 'brc-balance-list',
      // onError: (e) => {
      //   console.error(e)
      //   // toast.error('Failed to fetch token balance.')
      // },
    },
  )
}

export { useBrcTokenBalance, useBrcBalanceList }
